import {
  Anchor,
  Badge,
  Card,
  Group,
  Image,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Modal,
  Button,
} from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  IconActivity,
  IconArrowRight,
  IconHeartbeat,
  IconTemperature,
  IconVaccine,
  IconPhoto,
  IconClock,
  IconSearch,
} from "@tabler/icons-react";
import { usePatients } from "../../hooks/usePatients";
import { usePatientSummary } from "../../hooks/usePatientInsights";
import { usePatientTrend } from "../../hooks/usePatientTrend";
import { usePatientGraphPaths } from "../../hooks/usePatientGraphPaths";
import { usePatientStore } from "../../stores/patientStore";
import { useInsulinData } from "../../hooks/useInsulinData";
import { useECGFeatures } from "../../hooks/useECGFeatures";
import { useMealImages } from "../../hooks/useMealImages";
import { EnhancedPatientSelector } from "../../components/patient/EnhancedPatientSelector";
import type {
  GraphPathInsight,
  GraphPathRelationship,
  ECGFeatures,
  MealImage,
} from "../../types/api.ts";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export function DashboardPage(): JSX.Element {
  const [showPatientSelector, setShowPatientSelector] = useState(false);
  const { data: patientsResponse } = usePatients();
  const patients = useMemo(
    () => patientsResponse?.patients || [],
    [patientsResponse],
  );
  const selectedPatientId = usePatientStore((state) => state.selectedPatientId);
  const selectPatient = usePatientStore((state) => state.selectPatient);

  useEffect(() => {
    if (!selectedPatientId && patients && patients.length > 0) {
      selectPatient(patients[0].patientId);
    }
  }, [selectedPatientId, patients, selectPatient]);

  const { data: summary, isLoading: summaryLoading } =
    usePatientSummary(selectedPatientId);
  const {
    data: trend,
    isLoading: trendLoading,
    isFetching: trendFetching,
  } = usePatientTrend(selectedPatientId, 168);
  const {
    data: graphPaths,
    isLoading: pathsLoading,
    isFetching: pathsFetching,
  } = usePatientGraphPaths(selectedPatientId);
  const primaryPath = useMemo(
    () => (graphPaths && graphPaths.length > 0 ? graphPaths[0] : undefined),
    [graphPaths],
  );
  const secondaryPathCount = useMemo(
    () => (graphPaths && graphPaths.length > 1 ? graphPaths.length - 1 : 0),
    [graphPaths],
  );

  // New data hooks for enhanced dashboard
  const {
    data: insulinData,
    getInsulinSummary,
    isLoading: insulinLoading,
  } = useInsulinData(selectedPatientId, {
    enabled: Boolean(selectedPatientId),
  });
  const insulinSummary = useMemo(
    () => getInsulinSummary?.(),
    [getInsulinSummary],
  );

  const {
    data: ecgData,
    getECGSummary,
    isLoading: ecgLoading,
  } = useECGFeatures(selectedPatientId, {
    enabled: Boolean(selectedPatientId),
  });
  const ecgSummary = useMemo(() => getECGSummary?.(), [getECGSummary]);

  const {
    data: mealImages,
    getMealImagesSummary,
    isLoading: mealsLoading,
  } = useMealImages(selectedPatientId, {
    enabled: Boolean(selectedPatientId),
    limit: 6,
  });
  const mealsSummary = useMemo(
    () => getMealImagesSummary?.(),
    [getMealImagesSummary],
  );

  const selectedPatient = useMemo(() => {
    if (!selectedPatientId || !patients) return null;
    return patients.find((p) => p.patientId === selectedPatientId);
  }, [selectedPatientId, patients]);
  const dataCompleteness = selectedPatient?.dataCompletenessScore ?? 0;
  const extractTimestamp = useCallback(
    (entry: {
      recordedAt?: string;
      timestamp?: string;
    }): string | undefined => {
      if (entry.recordedAt) return entry.recordedAt;
      return entry.timestamp;
    },
    [],
  );

  const toUnixTime = useCallback(
    (value?: string) => (value ? new Date(value).getTime() : 0),
    [],
  );

  const lastInsulinDoseTime = insulinSummary?.mostRecentMeasurement
    ? extractTimestamp(insulinSummary.mostRecentMeasurement)
    : undefined;

  return (
    <Stack gap="xl">
      <Group justify="space-between">
        <div>
          <Title order={2}>Patient Overview</Title>
          <Text c="dimmed">
            Comprehensive view of glucose control, insulin tracking, ECG
            monitoring, meal images, and lifestyle signals derived from the
            PHKG.
          </Text>
          {selectedPatient && (
            <Group gap="xs" mt="xs">
              <Badge
                color={selectedPatient.cohort === "diabetes" ? "red" : "green"}
                variant="light"
              >
                {selectedPatient.cohort}
              </Badge>
              <Badge color="blue" variant="light">
                {selectedPatient.glucoseReadings} glucose
              </Badge>
              <Badge color="red" variant="light">
                {selectedPatient.ecgReadings} ECG
              </Badge>
              <Badge color="green" variant="light">
                {selectedPatient.insulinMeasurements} insulin
              </Badge>
              <Badge color="orange" variant="light">
                {selectedPatient.mealImages} meals
              </Badge>
              <Badge
                color={
                  dataCompleteness >= 75
                    ? "green"
                    : dataCompleteness >= 50
                      ? "yellow"
                      : dataCompleteness >= 25
                        ? "orange"
                        : "red"
                }
                variant="dot"
              >
                {dataCompleteness}% complete
              </Badge>
            </Group>
          )}
        </div>
        <Button
          variant="light"
          leftSection={<IconSearch size={16} />}
          onClick={() => setShowPatientSelector(true)}
        >
          {selectedPatient
            ? `${selectedPatient.name} (${selectedPatient.patientId})`
            : "Select Patient"}
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
        <InsightCard
          loading={summaryLoading}
          label="Glucose Control"
          value={
            summary?.glucose.latestValue
              ? `${summary.glucose.latestValue.toFixed(1)} mg/dL`
              : "—"
          }
          description={
            summary?.glucose.latestTimestamp
              ? `Latest reading ${dayjs(summary.glucose.latestTimestamp).fromNow()}`
              : "Awaiting sensor data"
          }
          icon={<IconTemperature size={20} />}
          badge={
            summary?.glucose.average
              ? `Avg ${summary.glucose.average.toFixed(1)} mg/dL`
              : undefined
          }
        />

        <InsightCard
          loading={insulinLoading}
          label="Insulin Today"
          value={
            insulinSummary?.totalUnits
              ? `${insulinSummary.totalUnits.toFixed(1)} units`
              : "-"
          }
          description={
            insulinSummary?.mostRecentMeasurement && lastInsulinDoseTime
              ? `Last dose ${dayjs(lastInsulinDoseTime).fromNow()}`
              : "No insulin recorded"
          }
          icon={<IconVaccine size={20} />}
          badge={
            insulinSummary?.rapidUnits && insulinSummary?.longActingUnits
              ? `${insulinSummary.rapidUnits.toFixed(1)} rapid / ${insulinSummary.longActingUnits.toFixed(1)} basal`
              : undefined
          }
        />

        <InsightCard
          loading={ecgLoading}
          label="Heart Monitoring"
          value={
            ecgSummary?.averageHeartRate
              ? `${ecgSummary.averageHeartRate} bpm`
              : "—"
          }
          description={
            ecgSummary?.mostRecentRecording
              ? `Last reading ${dayjs(ecgSummary.mostRecentRecording.timestamp).fromNow()}`
              : "No ECG data"
          }
          icon={<IconHeartbeat size={20} />}
          badge={
            ecgSummary?.averageHRV
              ? `HRV ${ecgSummary.averageHRV.toFixed(1)} ms`
              : undefined
          }
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
        <InsightCard
          loading={summaryLoading}
          label="Lifestyle Signals"
          value={summary?.lifestyle.mealCount ?? 0}
          description={
            summary?.lifestyle.lastMealTimestamp
              ? `Last meal ${dayjs(summary.lifestyle.lastMealTimestamp).fromNow()}`
              : "No recent meals logged"
          }
          icon={<IconHeartbeat size={20} />}
          badge={summary?.lifestyle.lastMeal ?? undefined}
        />

        <InsightCard
          loading={mealsLoading}
          label="Meal Images"
          value={mealsSummary?.totalImages ?? 0}
          description={
            mealsSummary?.mostRecentImage
              ? `Last image ${dayjs(mealsSummary.mostRecentImage.timestamp).fromNow()}`
              : "No meal images"
          }
          icon={<IconPhoto size={20} />}
          badge={
            mealsSummary?.totalSizeMB
              ? `${mealsSummary.totalSizeMB.toFixed(1)} MB`
              : undefined
          }
        />

        <InsightCard
          loading={summaryLoading}
          label="Sensor Coverage"
          value={summary?.sensor.recordings ?? 0}
          description={
            summary?.sensor.recentRecording?.startTimestamp
              ? `Latest ${summary.sensor.recentRecording.recordType} at ${dayjs(
                  summary.sensor.recentRecording.startTimestamp,
                ).format("HH:mm")}`
              : "No sensor recordings"
          }
          icon={<IconHeartbeat size={20} />}
        />
      </SimpleGrid>

      <Paper p="lg" radius="md" withBorder>
        <Group justify="space-between" mb="sm">
          <div>
            <Title order={4}>Weekly Glucose Trend</Title>
            <Text size="sm" c="dimmed">
              Rolling 7-day time series from CGM and fingerstick readings.
            </Text>
          </div>
          {trendFetching && <Loader size="sm" />}
        </Group>

        <Stack gap="sm">
          {trendLoading && <Loader />}
          {!trendLoading && trend && trend.length === 0 && (
            <Text size="sm" c="dimmed">
              No glucose readings found for this period.
            </Text>
          )}
          {!trendLoading && trend && trend.length > 0 && (
            <Stack gap="xs">
              {trend
                .slice(-6)
                .reverse()
                .map((point) => (
                  <Group
                    key={`${point.timestamp}-${point.glucose}`}
                    justify="space-between"
                  >
                    <Text>{dayjs(point.timestamp).format("MMM D, HH:mm")}</Text>
                    <Group gap="xs">
                      <Badge color="teal" variant="light">
                        {point.glucose.toFixed(1)} {point.unit}
                      </Badge>
                    </Group>
                  </Group>
                ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Insulin Tracking Section */}
      <Paper p="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Title order={4}>Insulin Tracking</Title>
              <Text size="sm" c="dimmed">
                Recent insulin doses and daily totals by type.
              </Text>
            </div>
            {insulinLoading && <Loader size="sm" />}
          </Group>

          {insulinLoading && <Loader />}
          {!insulinLoading && (!insulinData || insulinData.length === 0) && (
            <Text size="sm" c="dimmed">
              No insulin measurements recorded for this patient.
            </Text>
          )}
          {!insulinLoading && insulinData && insulinData.length > 0 && (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              {insulinData
                .sort(
                  (a, b) =>
                    toUnixTime(extractTimestamp(b)) -
                    toUnixTime(extractTimestamp(a)),
                )
                .slice(0, 6)
                .map((dose) => {
                  const doseTimestamp = extractTimestamp(dose);

                  return (
                    <Card key={dose.id} withBorder padding="sm" radius="md">
                      <Group justify="space-between" mb="xs">
                        <Badge
                          color={
                            dose.type === "rapid"
                              ? "blue"
                              : dose.type === "long-acting"
                                ? "green"
                                : dose.type === "premixed"
                                  ? "orange"
                                  : "gray"
                          }
                          variant="light"
                          size="sm"
                        >
                          {dose.type}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {doseTimestamp
                            ? dayjs(doseTimestamp).format("HH:mm")
                            : "--"}
                        </Text>
                      </Group>
                      <Text fw={600} size="lg">
                        {dose.units} units
                      </Text>
                      {dose.injectionSite && (
                        <Text size="xs" c="dimmed" mt="xs">
                          Site: {dose.injectionSite}
                        </Text>
                      )}
                      {dose.notes && (
                        <Text size="xs" c="dimmed" mt="xs">
                          {dose.notes}
                        </Text>
                      )}
                    </Card>
                  );
                })}
            </SimpleGrid>
          )}
        </Stack>
      </Paper>

      {/* ECG Monitoring Section */}
      <Paper p="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Title order={4}>ECG Monitoring</Title>
              <Text size="sm" c="dimmed">
                Heart rate trends, HRV metrics, and rhythm classification.
              </Text>
            </div>
            {ecgLoading && <Loader size="sm" />}
          </Group>

          {ecgLoading && <Loader />}
          {!ecgLoading && (!ecgData || ecgData.length === 0) && (
            <Text size="sm" c="dimmed">
              No ECG recordings available for this patient.
            </Text>
          )}
          {!ecgLoading && ecgData && ecgData.length > 0 && (
            <Stack gap="md">
              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                <Card withBorder padding="sm" radius="md">
                  <Group gap="xs" mb="xs">
                    <IconHeartbeat size={16} />
                    <Text size="sm" fw={600}>
                      Heart Rate
                    </Text>
                  </Group>
                  <Text size="lg" fw={700}>
                    {ecgSummary?.averageHeartRate || 0} bpm
                  </Text>
                  <Text size="xs" c="dimmed">
                    Average
                  </Text>
                </Card>

                <Card withBorder padding="sm" radius="md">
                  <Group gap="xs" mb="xs">
                    <IconActivity size={16} />
                    <Text size="sm" fw={600}>
                      HRV
                    </Text>
                  </Group>
                  <Text size="lg" fw={700}>
                    {ecgSummary?.averageHRV?.toFixed(1) || 0} ms
                  </Text>
                  <Text size="xs" c="dimmed">
                    RMSSD
                  </Text>
                </Card>

                <Card withBorder padding="sm" radius="md">
                  <Group gap="xs" mb="xs">
                    <IconHeartbeat size={16} />
                    <Text size="sm" fw={600}>
                      Rhythm
                    </Text>
                  </Group>
                  <Badge
                    color={
                      ecgSummary?.normalRhythmCount &&
                      ecgSummary.normalRhythmCount > ecgSummary.arrhythmiaCount
                        ? "green"
                        : "yellow"
                    }
                    variant="light"
                    size="sm"
                  >
                    {ecgSummary?.normalRhythmCount || 0} normal
                  </Badge>
                  {ecgSummary?.arrhythmiaCount &&
                    ecgSummary.arrhythmiaCount > 0 && (
                      <Text size="xs" c="orange" mt="xs">
                        {ecgSummary.arrhythmiaCount} arrhythmia
                      </Text>
                    )}
                </Card>

                <Card withBorder padding="sm" radius="md">
                  <Group gap="xs" mb="xs">
                    <IconClock size={16} />
                    <Text size="sm" fw={600}>
                      Recordings
                    </Text>
                  </Group>
                  <Text size="lg" fw={700}>
                    {ecgSummary?.totalRecordings || 0}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Total
                  </Text>
                </Card>
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                {ecgData
                  .sort(
                    (a, b) =>
                      new Date(b.timestamp).getTime() -
                      new Date(a.timestamp).getTime(),
                  )
                  .slice(0, 3)
                  .map((recording: ECGFeatures) => (
                    <Card
                      key={recording.id}
                      withBorder
                      padding="sm"
                      radius="md"
                    >
                      <Group justify="space-between" mb="xs">
                        <Badge
                          color={
                            recording.waveformFeatures.rhythmClassification ===
                            "normal"
                              ? "green"
                              : recording.waveformFeatures
                                    .rhythmClassification === "arrhythmia"
                                ? "red"
                                : "yellow"
                          }
                          variant="light"
                          size="sm"
                        >
                          {recording.waveformFeatures.rhythmClassification}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {dayjs(recording.timestamp).format("HH:mm")}
                        </Text>
                      </Group>
                      <Group gap="md">
                        <div>
                          <Text size="sm" c="dimmed">
                            Heart Rate
                          </Text>
                          <Text fw={600}>{recording.heartRate} bpm</Text>
                        </div>
                        <div>
                          <Text size="sm" c="dimmed">
                            HRV
                          </Text>
                          <Text fw={600}>
                            {recording.hrvFeatures?.heartRateVariability !==
                            undefined
                              ? `${recording.hrvFeatures.heartRateVariability.toFixed(1)} ms`
                              : "--"}
                          </Text>
                        </div>
                      </Group>
                    </Card>
                  ))}
              </SimpleGrid>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Meal Images Gallery Section */}
      <Paper p="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Title order={4}>Meal Images Gallery</Title>
              <Text size="sm" c="dimmed">
                Recent meal photos with timing and glucose impact correlation.
              </Text>
            </div>
            {mealsLoading && <Loader size="sm" />}
          </Group>

          {mealsLoading && <Loader />}
          {!mealsLoading && (!mealImages || mealImages.length === 0) && (
            <Text size="sm" c="dimmed">
              No meal images available for this patient.
            </Text>
          )}
          {!mealsLoading && mealImages && mealImages.length > 0 && (
            <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="sm">
              {mealImages
                .sort(
                  (a, b) =>
                    new Date(b.timestamp).getTime() -
                    new Date(a.timestamp).getTime(),
                )
                .slice(0, 6)
                .map((image: MealImage) => (
                  <Card key={image.id} withBorder padding="xs" radius="md">
                    <Stack gap="xs">
                      <div style={{ position: "relative" }}>
                        <Image
                          src={image.imageBase64}
                          alt={`Meal from ${dayjs(image.timestamp).format("MMM D, HH:mm")}`}
                          height={80}
                          fit="cover"
                          radius="sm"
                          fallbackSrc="data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='80' height='80' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999'%3ENo Image%3C/text%3E%3C/svg%3E"
                        />
                        <Badge
                          size="xs"
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                          }}
                          variant="light"
                        >
                          {dayjs(image.timestamp).format("HH:mm")}
                        </Badge>
                      </div>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {image.metadata?.description ||
                          `Meal ${dayjs(image.timestamp).format("MMM D")}`}
                      </Text>
                      {image.metadata?.format && (
                        <Text size="xs" c="dimmed">
                          {image.metadata.format.toUpperCase()}
                        </Text>
                      )}
                    </Stack>
                  </Card>
                ))}
            </SimpleGrid>
          )}
        </Stack>
      </Paper>

      <Paper p="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Title order={4}>Graph Insight</Title>
              <Text size="sm" c="dimmed">
                Shortest paths across the PHKG within three hops of the patient.
              </Text>
            </div>
            {(pathsFetching || pathsLoading) && <Loader size="sm" />}
          </Group>
          {pathsLoading && <Loader />}
          {!pathsLoading && !primaryPath && (
            <Text size="sm" c="dimmed">
              No related graph paths were found for this patient.
            </Text>
          )}
          {!pathsLoading && primaryPath && (
            <Stack gap="md">
              <GraphPathPreview path={primaryPath} />
              {secondaryPathCount > 0 && (
                <Badge color="teal" variant="outline" size="sm">
                  {secondaryPathCount} additional path
                  {secondaryPathCount > 1 ? "s" : ""} available
                </Badge>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="xs">
          <Title order={5}>Need richer graphs?</Title>
          <Text size="sm" c="dimmed">
            Deploy the Neo4j Bloom scene shipped in{" "}
            <Anchor href="/api/neo4j">/api/neo4j</Anchor> to explore the
            complete patient-health graph.
          </Text>
        </Stack>
      </Card>

      {/* Patient Selection Modal */}
      <Modal
        opened={showPatientSelector}
        onClose={() => setShowPatientSelector(false)}
        title="Select Patient"
        size="lg"
      >
        <EnhancedPatientSelector
          selectedPatientId={selectedPatientId}
          onPatientSelect={(patientId) => {
            selectPatient(patientId);
            setShowPatientSelector(false);
          }}
          showDataIndicators={true}
          showSearch={true}
          showFilters={true}
          showPagination={true}
          maxHeight={500}
        />
      </Modal>
    </Stack>
  );
}

interface InsightCardProps {
  loading: boolean;
  label: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  badge?: string;
}

function InsightCard({
  loading,
  label,
  value,
  description,
  icon,
  badge,
}: InsightCardProps) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="xs">
        <Group gap="sm">
          {icon}
          <Text fw={600}>{label}</Text>
        </Group>
        {loading ? (
          <Loader size="sm" />
        ) : (
          <>
            <Text fz="xl" fw={700}>
              {value}
            </Text>
            {description && (
              <Text size="sm" c="dimmed">
                {description}
              </Text>
            )}
            {badge && (
              <Badge color="teal" variant="light" tt="none">
                {badge}
              </Badge>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}

function GraphPathPreview({ path }: { path: GraphPathInsight }) {
  return (
    <Stack gap="sm">
      <Group gap="xs">
        <IconActivity size={18} />
        <Text fw={600}>{path.description}</Text>
      </Group>
      <Stack gap="lg">
        {path.nodes.map((node, index) => (
          <Stack key={`${node.label}-${index}`} gap="xs">
            <Group align="flex-start" gap="xs">
              <Badge color="teal" variant="light">
                {node.label}
              </Badge>
              <Text size="sm" c="dimmed">
                {summarizeProperties(node.properties)}
              </Text>
            </Group>
            {index < path.relationships.length && (
              <RelationshipRow relationship={path.relationships[index]} />
            )}
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

function RelationshipRow({
  relationship,
}: {
  relationship: GraphPathRelationship;
}) {
  return (
    <Group gap="xs" c="dimmed">
      <IconArrowRight size={16} />
      <Badge color="gray" variant="dot">
        {relationship.type}
      </Badge>
      <Text size="xs">{summarizeProperties(relationship.properties)}</Text>
    </Group>
  );
}

function summarizeProperties(properties: Record<string, unknown>): string {
  const importantKeys = [
    "patientId",
    "name",
    "description",
    "timestamp",
    "startTimestamp",
    "recordType",
    "cohort",
    "glucose",
  ];

  for (const key of importantKeys) {
    if (properties[key] !== undefined) {
      return `${key}: ${String(properties[key])}`;
    }
  }

  if (Object.keys(properties).length === 0) {
    return "No attributes";
  }

  try {
    const serialized = JSON.stringify(properties);
    return serialized.length > 80
      ? `${serialized.slice(0, 77)}...`
      : serialized;
  } catch {
    return "Complex attributes";
  }
}
